import { useEffect, useMemo, useState } from "react";
import { orderApi } from "../api/orderApi";
import { productApi } from "../api/productApi";

export default function OrdersList() {
  const [items, setItems] = useState([]);
  const [prodMap, setProdMap] = useState({}); // productId -> productNameAz (və ya nameAz)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const statusOptions = useMemo(
    () => [
      { v: "new", t: "Yeni" },
      { v: "contacted", t: "Əlaqə saxlanıldı" },
      { v: "done", t: "İcra olundu" },
      { v: "canceled", t: "İmtina edildi" },
    ],
    [],
  );
  const statusText = useMemo(
    () => ({
      new: "Yeni",
      contacted: "Əlaqə saxlanıldı",
      done: "İcra olundu",
      canceled: "İmtina edildi",
    }),
    [],
  );

  const buildProdMap = (products) => {
    const map = {};
    (products || []).forEach((p) => {
      const name = p?.nameAz ?? p?.NameAz ?? `#${p?.id}`;
      map[p.id] = name;
    });
    return map;
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      // Orders + Products paralel
      const [ordRes, prRes] = await Promise.all([
        orderApi.getAll(),
        productApi.getAll(),
      ]);

      const orders = ordRes.data || [];
      const products = prRes.data || [];

      // newest first
      const sortedOrders = orders
        .slice()
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

      setItems(sortedOrders);
      setProdMap(buildProdMap(products));
    } catch (e) {
      setErr(e?.response?.data || e.message || "Orders load failed");
      setItems([]);
      setProdMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatDateTime = (v) => {
    if (!v) return "-";
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return "-";

    // ✅ +4 saat əlavə et (günü aşsa avtomatik növbəti günə keçir)
    dt.setHours(dt.getHours() + 4);

    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();

    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");

    return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
  };

  const setStatus = async (id, status) => {
    try {
      await orderApi.updateStatus(id, status);
      // local update (tez)
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) {
      alert(e?.response?.data || e.message || "Status update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin?")) return;
    try {
      await orderApi.remove(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      alert(e?.response?.data || e.message || "Delete failed");
    }
  };

  const badgeStyle = (status) => {
    const s = String(status || "new").toLowerCase();
    if (s === "done")
      return {
        background: "rgba(0,200,83,.18)",
        border: "1px solid rgba(0,200,83,.35)",
      };
    if (s === "contacted")
      return {
        background: "rgba(24,144,255,.18)",
        border: "1px solid rgba(24,144,255,.35)",
      };
    if (s === "canceled")
      return {
        background: "rgba(255,77,79,.16)",
        border: "1px solid rgba(255,77,79,.35)",
      };
    return {
      background: "rgba(255,255,255,.08)",
      border: "1px solid rgba(255,255,255,.14)",
    };
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h1 className="pageTitle" style={{ margin: 0 }}>
          Sifarişlər
        </h1>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {err ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="cardBody" style={{ color: "rgba(255,77,79,.9)" }}>
            {String(err)}
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 14 }}>
        {loading ? (
          <div className="card">
            <div className="cardBody">Yüklənir...</div>
          </div>
        ) : (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th style={{ width: 220 }}>Məhsulun adı</th>
                  <th style={{ width: 170 }}>Daxilolma tarixi</th>
                  <th style={{ width: 220 }}>Alıcının ad və soyadı</th>
                  <th style={{ width: 260 }}>Əlaqə vasitələri</th>
                  <th>Qeyd</th>
                  <th style={{ width: 180 }}>Status</th>
                  <th style={{ width: 120 }}>Əməliyyat</th>
                </tr>
              </thead>

              <tbody>
                {items.map((x, index) => (
                  <tr key={x.id}>
                    <td>{index + 1}</td>

                    <td style={{ fontWeight: 800 }}>
                      <div style={{ opacity: 0.85 }}>
                        {prodMap[x.productId] ?? `#${x.productId}`}
                      </div>
                      <div style={{ opacity: 0.6, fontSize: 12 }}>
                        Məhsul id-si: {x.productId}
                      </div>
                    </td>

                    <td style={{ opacity: 0.8 }}>
                      {x.createDateUtc ? formatDateTime(x.createDateUtc) : "-"}
                    </td>

                    <td>
                      <div style={{ fontWeight: 800 }}>
                        {x.name} {x.surname}
                      </div>
                    </td>

                    <td style={{ opacity: 0.85 }}>
                      <div>{x.email}</div>
                      <div>{x.phone}</div>
                    </td>

                    <td style={{ maxWidth: 420, opacity: 0.85 }}>
                      {x.note ? (
                        String(x.note)
                      ) : (
                        <span style={{ opacity: 0.55 }}>-</span>
                      )}
                    </td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          className="badge"
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            fontWeight: 800,
                            ...badgeStyle(x.status),
                          }}
                        >
                          {statusText[
                            String(x.status || "new").toLowerCase()
                          ] ?? String(x.status || "new")}
                        </span>

                        <select
                          className="input selectDark"
                          value={x.status || "new"}
                          onChange={(e) => setStatus(x.id, e.target.value)}
                          style={{ minWidth: 140, background: "#111827" }}
                        >
                          {statusOptions.map((s) => (
                            <option key={s.v} value={s.v}>
                              {s.t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                    <td>
                      <button
                        className="btn btnDanger"
                        onClick={() => del(x.id)}
                      >
                        Sil
                      </button>
                    </td>
                  </tr>
                ))}

                {items.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ color: "rgba(255,255,255,.6)" }}>
                      Heç sifariş yoxdur.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
